import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  contact as defaultContact,
  winterHours as defaultHours,
  rates as defaultRates,
  golfEvents,
  banquets as defaultBanquets,
  aboutContent,
  memberships as defaultMemberships,
  juniorGolf as defaultJuniorGolf,
  juniorCamp as defaultJuniorCamp,
  sandTrap as defaultSandTrap,
  homepage as defaultHomepage,
  membershipDocuments as defaultMembershipDocuments,
  siteImages,
} from "../data/redlandGolfContent";

const API = process.env.REACT_APP_BACKEND_URL;
const ContentContext = createContext(null);

const defAbout = { ...aboutContent, heroImage: siteImages.palmCourse };
const defMems = { ...defaultMemberships, heroImage: siteImages.clubhouse };
const defJG = { ...defaultJuniorGolf, heroImage: siteImages.junior };
const defJC = { ...defaultJuniorCamp, heroImage: siteImages.junior };
const defST = { ...defaultSandTrap, heroImage: siteImages.dining };

function isPlainObject(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

// Deep-merge override onto defaults (arrays are replaced wholesale).
function deepMerge(def, over) {
  if (!isPlainObject(over)) return over === undefined ? def : over;
  if (!isPlainObject(def)) return over;
  const out = { ...def };
  for (const key of Object.keys(over)) {
    out[key] = deepMerge(def[key], over[key]);
  }
  return out;
}

const isPreviewMode = () =>
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("preview") === "1";

function notExpired(e) {
  if (!e.expirationDate) return true;
  const t = new Date(e.expirationDate).getTime();
  if (Number.isNaN(t)) return true;
  // keep visible through the end of the expiration day
  return t + 24 * 60 * 60 * 1000 >= Date.now();
}

export function ContentProvider({ children }) {
  const [overrides, setOverrides] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [preview, setPreview] = useState(false);

  const refresh = useCallback(async () => {
    const usePreview = isPreviewMode();
    try {
      const url = usePreview
        ? `${API}/api/admin/preview?t=${Date.now()}`
        : `${API}/api/content?t=${Date.now()}`;
      const res = await fetch(url, { cache: "no-store", credentials: "include" });
      if (usePreview && res.status === 401) {
        // Not authenticated for preview — fall back to published content.
        const pub = await fetch(`${API}/api/content?t=${Date.now()}`, { cache: "no-store" });
        setOverrides((await pub.json()) || {});
        setPreview(false);
      } else {
        setOverrides((await res.json()) || {});
        setPreview(usePreview);
      }
    } catch {
      setOverrides({});
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const o = overrides || {};
  const merge = (def, key) => (o[key] ? { ...def, ...o[key] } : def);

  const rawEvents = Array.isArray(o.events) ? o.events : golfEvents.events;
  const publicEvents = rawEvents
    .filter((e) => e.visible !== false && notExpired(e))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const featured = publicEvents.filter((e) => e.featured);
  const homeEvents = (featured.length > 0 ? featured : publicEvents).slice(0, 3);

  const value = {
    loaded,
    preview,
    overrides: o,
    refresh,
    contact: merge(defaultContact, "contact"),
    hours: merge(defaultHours, "hours"),
    rates: merge(defaultRates, "rates"),
    events: publicEvents,
    homeEvents,
    featuredEvents: featured,
    banquets: merge(defaultBanquets, "banquets"),
    about: merge(defAbout, "about"),
    memberships: merge(defMems, "memberships"),
    juniorGolf: merge(defJG, "juniorGolf"),
    juniorCamp: merge(defJC, "juniorCamp"),
    sandTrap: merge(defST, "sandTrap"),
    announcements: o.announcements || { enabled: false, items: [] },
    homepage: deepMerge(defaultHomepage, o.homepage),
    membershipDocuments: deepMerge(defaultMembershipDocuments, o.membershipDocuments),
  };

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useSiteContent() {
  return useContext(ContentContext);
}
